import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { notificationService } from "@/services/notificationService";
import { Bell, CheckCheck } from "lucide-react";

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

export function NotificationsBell() {
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ["notifications"],
    queryFn: notificationService.list,
    refetchInterval: 30000,
  });
  const items = data?.notifications || [];
  const unread = data?.unread || 0;

  const invalidate = () => qc.invalidateQueries({ queryKey: ["notifications"] });
  const readOne = useMutation({ mutationFn: notificationService.read, onSuccess: invalidate });
  const readAll = useMutation({ mutationFn: notificationService.readAll, onSuccess: invalidate });

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          className="relative grid place-items-center h-9 w-9 rounded-full border border-border text-muted-foreground hover:text-foreground hover:border-foreground transition-colors"
          data-testid="notifications-bell" aria-label="Notifications"
        >
          <Bell className="h-4 w-4" />
          {unread > 0 && (
            <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 grid place-items-center text-[10px] font-bold rounded-full bg-primary text-primary-foreground" data-testid="notifications-unread-count">
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0 rounded-none" data-testid="notifications-panel">
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <span className="font-display font-bold text-sm">Notifications</span>
          {unread > 0 && (
            <button onClick={() => readAll.mutate()} className="text-xs text-primary font-semibold inline-flex items-center gap-1 hover:underline" data-testid="notifications-mark-all">
              <CheckCheck className="h-3.5 w-3.5" /> Mark all read
            </button>
          )}
        </div>
        <div className="max-h-80 overflow-y-auto">
          {items.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-10" data-testid="notifications-empty">You're all caught up.</p>
          ) : (
            items.map((n) => (
              <button
                key={n.id}
                onClick={() => !n.read && readOne.mutate(n.id)}
                className={`w-full text-left px-4 py-3 border-b border-border/60 transition-colors hover:bg-secondary/60 ${n.read ? "opacity-60" : "bg-primary/5"}`}
                data-testid={`notification-item-${n.id}`}
              >
                <div className="flex items-start gap-2">
                  {!n.read && <span className="mt-1.5 h-2 w-2 rounded-full bg-primary shrink-0" />}
                  <div className="min-w-0">
                    {n.title && <p className="text-sm font-semibold leading-tight">{n.title}</p>}
                    <p className="text-sm text-muted-foreground leading-snug">{n.body}</p>
                    <p className="text-[11px] text-muted-foreground/70 mt-1">{timeAgo(n.created_at)}</p>
                  </div>
                </div>
              </button>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
